import { createSelector } from 'reselect'
import Big from 'big.js'

import {
  assignedClassAllocations,
  allClassAllocations,
  UNASSIGNED_ASSETS,
  formatClassName,
} from 'utils/Client/classAllocationsConfig.js'

import { assetsOrder } from 'utils/Client/classAllocationsConfig'

const sortByDecimal = (a,b) => {
  const aDecimals = Math.abs(Math.floor(a.percentage) - a.percentage)
  const bDecimals = Math.abs(Math.floor(b.percentage) - b.percentage)
  return aDecimals - bDecimals
}

/** Function implementing Largest Remainder Method to make rounded percentages add up to 100% */
const roundClassAllocationPercentages = (classAllocations) => {
  let percentageDiff = 100 - classAllocations.reduce((acc, classAllocation) => (
    Math.floor(classAllocation.percentage) + acc
  ), 0)
  const classAllocationsResult = [...classAllocations]
    .sort(sortByDecimal)
    .map((classAllocation) => {
      const updatedPercentage = percentageDiff > 0 && classAllocation.percentage !== 0 ?
        Math.floor(classAllocation.percentage) + 1 : Math.floor(classAllocation.percentage)
      const newClassAllocation = {
        ...classAllocation,
        percentage: updatedPercentage
      }
      if (classAllocation.percentage !== 0) {
        percentageDiff -= 1
      }
      return newClassAllocation
    })
  return classAllocationsResult
}

const sortByClassName = (a, b) => {
  const aAsset = assetsOrder.find(asset => asset.className === a.className)
  const bAsset = assetsOrder.find(asset => asset.className === b.className)
  if (aAsset && bAsset) {
    return aAsset.weight - bAsset.weight
  }
  return -1
}

const orderAssetClasses = assetClasses => (
  assetClasses.map(e => e).sort(sortByClassName)
)

const sortByAssetName = (a,b) => {
  if (a.attributes.assetName > b.attributes.assetName) {
    return 1
  } else if (b.attributes.assetName > a.attributes.assetName) {
    return -1
  }
  return 0
}

const consolidateHoldings = (classHoldingGroup, newHolding) => {
  const { classHoldings } =  classHoldingGroup
  const {
    assetName,
    marketValue
  } = newHolding

  let wasConsolidated = false
  let newClassHoldings = classHoldings.map((holding) => {
    let updatedHolding = { ...holding }
    if (holding.assetName  === assetName) {
      updatedHolding = {
        ...updatedHolding,
        marketValue: holding.marketValue + marketValue
      }
      wasConsolidated = true
    }
    return updatedHolding
  })
  if (!wasConsolidated) {
    newClassHoldings = [...newClassHoldings, newHolding]
  }
  return newClassHoldings
}

const groupHoldings = holdings => (
  holdings.reduce((acc, holding) => {
    const {
      attributes: {
        className,
        assetName,
        marketValue,
      }
    } = holding
    let classNameFormatted = formatClassName(className)
    // If classNameFormatted is not from assignedClasses, make it Unassigned
    const classAllocationConfig = assignedClassAllocations.find(c => c.name === classNameFormatted)
    if (!classAllocationConfig) {
      classNameFormatted = UNASSIGNED_ASSETS.name
    }

    const classHoldingGroup = acc.find(c => c.className === classNameFormatted)
    let newClassHoldingGroup
    let updatedAcc = [...acc]

    if (classHoldingGroup) {
      updatedAcc = acc.filter(c => c.className !== classNameFormatted)
      newClassHoldingGroup = {
        ...classHoldingGroup,
        totalMarketValue: classHoldingGroup.totalMarketValue + marketValue,
        classHoldings: consolidateHoldings(classHoldingGroup, { assetName, marketValue })
      }
    } else {
      newClassHoldingGroup = {
        className: classNameFormatted,
        totalMarketValue: marketValue,
        classHoldings: [{
          assetName,
          marketValue,
        }]
      }
    }
    return [...updatedAcc, newClassHoldingGroup]
  }, [])
)


const getPercentage = (total, number, numberOfDecimals) => {
  if (total && number) {
    const numberValue = new Big(number)
    const totalValue = new Big(total)
    const percentage = numberValue
      .div(totalValue)
      .times(new Big(100))
    return parseFloat(percentage.toFixed(numberOfDecimals), 10)
  }
  return 0
}

export const addPercentages = (data, totalValue, property, decimals) => {
  let updatedData = data
  updatedData = data.map((dataItem) => {
    const value = dataItem[property]
    const percentage = getPercentage(totalValue, value, decimals)
    return {
      ...dataItem,
      percentage,
    }
  })
  return updatedData
}

export const computeHoldingsPercentages = (totalPortfolioEmv, holdings) => (
  holdings.map((holdingGroup) => {
    const {
      classHoldings
    } = holdingGroup
    let updatedClassHoldings = classHoldings

    updatedClassHoldings = addPercentages(classHoldings, totalPortfolioEmv, 'marketValue', 2)

    return {
      ...holdingGroup,
      classHoldings: updatedClassHoldings,
    }
  })
)

export const computeClassAllocationsPercentages = (totalPortfolioEmv, classAllocations) => {
  let  updatedClassAllocations = classAllocations

  if (totalPortfolioEmv) {
    updatedClassAllocations = addPercentages(classAllocations, totalPortfolioEmv, 'totalEmv', 2)
    return roundClassAllocationPercentages(updatedClassAllocations)
  }
  return updatedClassAllocations
}

const processHoldings = (holdings, totalPortfolioEmv) => {
  const holdingsFiltered = holdings.filter(h => h.attributes.marketValue)
  const holdingsAlphabetized = holdingsFiltered.sort(sortByAssetName)
  const holdingsGrouped = groupHoldings(holdingsAlphabetized)
  const holdingsWithPercentages = computeHoldingsPercentages(totalPortfolioEmv, holdingsGrouped)
  return orderAssetClasses(holdingsWithPercentages)
}

const separateAssets = classAllocations => (
  classAllocations.reduce((acc, allocation) => {
    const isAssigned = !!assignedClassAllocations.find(a => a.name === allocation.className)
    if (isAssigned) {
      return {
        assignedAssets: [...acc.assignedAssets, allocation],
        unassignedAssets: [...acc.unassignedAssets]
      }
    } else {
      return {
        assignedAssets: [...acc.assignedAssets],
        unassignedAssets: [...acc.unassignedAssets, allocation]
      }
    }
  },
  {
    assignedAssets: [],
    unassignedAssets: []
  }
  )
)

const reduceUnassignedAssets = unassignedMock => (
  unassignedMock.reduce((acc, a) => {
    const accTotalEmv = new Big(acc.totalEmv)
    const assetTotalEmv = new Big(a.totalEmv)
    const toltaEmvSum = parseFloat(accTotalEmv.plus(assetTotalEmv).toString())
    return {
      ...acc,
      totalEmv: toltaEmvSum
    }
  } , {
    classId: '0',
    className: UNASSIGNED_ASSETS.name,
    totalEmv: 0,
    type: UNASSIGNED_ASSETS.type
  })
)

const addMissingClassAllocations = classAllocations => (
  allClassAllocations.map((configClassAllocation) => {
    const classAllocationFound = classAllocations
      .find(c => c.className === configClassAllocation.name)

    if (!classAllocationFound && configClassAllocation.name !== UNASSIGNED_ASSETS.name) {
      return {
        classId: null,
        totalEmv: 0,
        className: configClassAllocation.name,
      }
    } else {
      return classAllocationFound
    }
  }).filter(c => !!c)
)

export const addUnassignedAssets = (classAllocations) => {
  const allocationsSeparated = separateAssets(classAllocations)
  let unassignedAssetsGrouped = {}
  if (allocationsSeparated.unassignedAssets.length > 0) {
    unassignedAssetsGrouped = reduceUnassignedAssets(allocationsSeparated.unassignedAssets)
    return [...allocationsSeparated.assignedAssets, { ...unassignedAssetsGrouped }]
  }
  return [...allocationsSeparated.assignedAssets]
}

const getPrimaryPortfolioDetails = state => state.primaryPortfolio
const getPrimaryPortfolioAccounts = state => state.primaryPortfolioAccounts
const getPrimaryPortfolioClassAllocations = state => state.primaryPortfolioClassAllocations
const getPrimaryPortfolioHoldings = state => state.primaryPortfolioHoldings

const processClassAllocations = (classAllocations, totalEmv) => {
  const classAllocationsWithUnassignedAssets = addUnassignedAssets(classAllocations)
  const classAllocationsCompleted = addMissingClassAllocations(classAllocationsWithUnassignedAssets)
  const updatedClassAllocations = computeClassAllocationsPercentages(
    totalEmv,
    classAllocationsCompleted
  )
  return orderAssetClasses(updatedClassAllocations)
}

export const getPrimaryPortfolio = createSelector(
  getPrimaryPortfolioDetails,
  getPrimaryPortfolioClassAllocations,
  getPrimaryPortfolioAccounts,
  getPrimaryPortfolioHoldings,
  (primaryPortfolioState,
    primaryPortfolioClassAllocsState,
    primaryPortfolioAccountsState,
    primaryPortfolioHoldingsState) => {
    const {
      primaryPortfolioClassAllocations : { classAllocations }
    } = primaryPortfolioClassAllocsState
    const {
      primaryPortfolio,
      primaryPortfolio: { totalEmv }
    } = primaryPortfolioState

    const {
      primaryPortfolioHoldings
    } = primaryPortfolioHoldingsState

    const processedClassAllocations = processClassAllocations(classAllocations, totalEmv)
    const processedHoldings = processHoldings(primaryPortfolioHoldings, totalEmv)

    return {
      ...primaryPortfolio,
      classAllocations: processedClassAllocations,
      financialAccounts: [...primaryPortfolioAccountsState.primaryPortfolioAccounts],
      holdings: processedHoldings,
    }
  }
)

export default {
  getPrimaryPortfolio
}



// WEBPACK FOOTER //
// ./src/selectors/onlineExperience/portfolio.js